import type { RowPath } from './arrow-rows'
import type {
  DescriptorInput,
  ExportRequest,
  WorkerOutbound
} from './export-worker-types'
import { stripNullSafeGroupBy } from './null-safe-group-by'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The descriptor input the report supplies. `groupBy` is derived here from the
 * actually-exported view config (so it always matches the Arrow's true depth),
 * not passed by the caller — hence omitted from the caller-facing shape.
 */
export type ExportDescriptorInput = Omit<DescriptorInput, 'groupBy'>

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Perspective's modern row-path key in `to_columns()` output. */
const ROW_PATH_KEY = '__ROW_PATH__'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The minimal Perspective viewer interface used by the client.
 * Both `getView()` and `getTable()` are needed to build a fully-expanded
 * temporary view for export — avoiding mutation of the live UI view.
 */
export type PerspectiveViewer = {
  getView(): Promise<PerspectiveView>
  getTable(): Promise<PerspectiveTable>
}

/** A Perspective view config — only the fields the export reads; extra fields are preserved by spread. */
export type ViewConfig = Readonly<Record<string, unknown>> & {
  readonly group_by?: readonly string[]
  readonly sort?: readonly (readonly [string, string])[]
}

/** The Perspective view interface subset used by the client. */
export type PerspectiveView = {
  get_config(): Promise<ViewConfig>
  set_depth(depth: number): Promise<void>
  to_arrow(window?: {
    emit_legacy_row_path_names?: boolean
  }): Promise<ArrayBuffer>
  to_columns(window?: unknown): Promise<Record<string, readonly unknown[]>>
  delete(): Promise<void>
}

/** The Perspective table interface subset used by the client. */
export type PerspectiveTable = {
  view(config: unknown): Promise<PerspectiveView>
}

// ---------------------------------------------------------------------------
// Full-expansion helpers
// ---------------------------------------------------------------------------

/**
 * Builds the config for the fully-expanded export view.
 *
 * A sort on a value/aggregate column reorders rows WITHIN a group level, which
 * breaks the contiguity Excel's row outline relies on — a single parent group
 * would split into several disconnected +/- bands. Sorts on group_by columns
 * reorder whole groups while keeping each subtree contiguous, so those are kept;
 * sorts on any other (value/aggregate) column are dropped for the export.
 *
 * The live grid groups STRING columns by a null-coalescing `__ng__*` expression
 * so null keys render as expandable groups. The export must NOT see those: it
 * groups by the RAW columns and labels the null key itself via `__ROW_PATH__`.
 * `stripNullSafeGroupBy` removes the expressions and maps every `__ng__<field>`
 * reference back to `<field>` before the sort filter runs.
 */
export const expandedExportConfig = (config: ViewConfig): ViewConfig => {
  const stripped = stripNullSafeGroupBy(config)

  const groupBy = stripped.group_by ?? []
  const sort = stripped.sort ?? []

  return { ...stripped, sort: sort.filter(([col]) => groupBy.includes(col)) }
}

/**
 * Produces an Arrow buffer that includes ALL leaf rows, regardless of how the
 * live view's group tree is currently collapsed in the UI.
 *
 * For flat views (no group_by) the live view is already complete — returned
 * directly. For grouped views a temporary view is created from the same table
 * and config, set to the maximum expansion depth, exported, then deleted.
 * The user's live view is never mutated, so the UI does not flicker and
 * per-row collapse state is preserved.
 *
 * Falls back to the live view's to_arrow() if the temporary view cannot be
 * constructed (e.g. unsupported config field).
 */
/**
 * Coerces the `__ROW_PATH__` column from a `to_columns()` result into the
 * per-row `RowPath[]` the worker decodes group depth from. Each entry is an
 * array of path elements; a null element is a legitimate null group key and is
 * preserved (so the worker can distinguish it from the grand total). A missing
 * column (flat view) yields an empty list.
 */
const extractRowPaths = (
  columns: Record<string, readonly unknown[]>
): readonly RowPath[] => {
  const raw = columns[ROW_PATH_KEY]

  if (raw === undefined) {
    return []
  }

  return raw.map((entry) =>
    Array.isArray(entry)
      ? entry.map((el) => (el === null || el === undefined ? null : String(el)))
      : []
  )
}

/**
 * Reads the authoritative per-row group paths from a view's modern
 * `__ROW_PATH__` (via `to_columns()`). This is the ONLY reliable depth source:
 * the Arrow buffer's flattened `__ROW_PATH_N__` columns collapse a null group
 * key and "above this level" both to null, losing the distinction between the
 * grand total, a null-key subtotal, and a leaf under a null group.
 */
const fetchRowPaths = async (
  view: PerspectiveView
): Promise<readonly RowPath[]> => {
  const columns = await view.to_columns()
  return extractRowPaths(columns)
}

const fullyExpandedArrow = async (
  viewer: PerspectiveViewer
): Promise<{
  arrow: ArrayBuffer
  groupBy: readonly string[]
  rowPaths: readonly RowPath[]
}> => {
  const liveView = await viewer.getView()
  const config = await liveView.get_config()

  // Strip the live grid's `__ng__` null-coalescing expressions ONCE and derive
  // the worker-bound `groupBy` from the stripped config — these are the RAW
  // field names (`category_title`, …), NOT the `__ng__category_title` sentinels.
  // The worker synthesizes "(Group by N)" headers from these names, so passing
  // the unstripped config_by would leak `__ng__…` into the exported headers
  // (toDisplayHeader can't map them back). The temp view is built from the SAME
  // stripped config, so its Arrow + group depth match the raw field names.
  const exportConfig = expandedExportConfig(config)
  const groupBy = exportConfig.group_by ?? []
  const depth = groupBy.length

  if (depth === 0) {
    const arrow = await liveView.to_arrow({ emit_legacy_row_path_names: false })
    return { arrow, groupBy, rowPaths: [] }
  }

  let fullView: PerspectiveView | undefined

  try {
    const table = await viewer.getTable()
    fullView = await table.view(exportConfig)
    await fullView.set_depth(depth)
    const arrow = await fullView.to_arrow({ emit_legacy_row_path_names: false })
    const rowPaths = await fetchRowPaths(fullView)
    return { arrow, groupBy, rowPaths }
  } catch (err) {
    console.warn(
      '[export] Could not build fully-expanded view, falling back to live view:',
      err
    )
    const arrow = await liveView.to_arrow({ emit_legacy_row_path_names: false })
    const rowPaths = await fetchRowPaths(liveView)
    return { arrow, groupBy, rowPaths }
  } finally {
    await fullView?.delete()
  }
}

// ---------------------------------------------------------------------------
// Worker lifecycle
// ---------------------------------------------------------------------------

/** A terminal worker response — every WorkerOutbound except interim progress. */
type TerminalOutbound = Exclude<WorkerOutbound, { kind: 'progress' }>

/**
 * Spawns the one-shot export worker, posts the request (transferring the Arrow
 * buffer zero-copy), forwards interim progress, and resolves with the FIRST
 * terminal message ('done' | 'csv' | 'error'). The worker is terminated after
 * that message. Callers map the terminal message to their own result type.
 *
 * The XLSX and CSV paths differ only in the `request.format` field and which
 * terminal message they expect — the spawn/transfer/teardown is identical, so
 * it lives here once.
 */
const runExportWorker = (
  request: ExportRequest,
  onProgress?: (processed: number, total: number) => void
): Promise<TerminalOutbound> =>
  new Promise((resolve, reject) => {
    // Reference the BUILT worker artifact (export-worker.js), not the .ts
    // source: build.ts emits export-worker.js to priv/static/assets via a
    // dedicated Bun.build, and Phoenix serves it at /assets/export-worker.js.
    // import.meta.url is the served app.js URL, so this resolves alongside it.
    // (The .ts source path is only served by the Playwright test harness, not
    // by Phoenix — referencing it broke worker loading in the real app.)
    const worker = new Worker(new URL('./export-worker.js', import.meta.url), {
      type: 'module'
    })

    worker.onmessage = (event: MessageEvent<WorkerOutbound>) => {
      const msg = event.data

      if (msg.kind === 'progress') {
        onProgress?.(msg.processed, msg.total)
        return
      }

      worker.terminate()
      resolve(msg)
    }

    worker.onerror = (event) => {
      worker.terminate()
      reject(new Error(event.message ?? 'Worker error'))
    }

    // Transfer the ArrayBuffer zero-copy — after this the main thread
    // no longer owns the buffer.
    worker.postMessage(request, [request.arrow])

    // The HTML spec neuters a transferred ArrayBuffer synchronously;
    // byteLength must be 0 immediately after postMessage returns.
    // A non-zero value would indicate the transfer was silently skipped
    // (e.g. the buffer was already detached or was a SharedArrayBuffer).
    if (request.arrow.byteLength !== 0) {
      console.warn(
        '[export] Arrow buffer not detached after transfer — main thread may retain a large duplicate',
        { byteLength: request.arrow.byteLength }
      )
    }
  })

/**
 * Spawns the export worker, sends it the transferred Arrow buffer and the
 * descriptor input (schema / columnsConfig / idColumns), and returns a Promise
 * that resolves to the xlsx Blob or rejects on error.
 *
 * The viewer (not a bare view) is accepted so that a fully-expanded temporary
 * view can be created for the export — ensuring collapsed groups in the UI do
 * not cause data loss in the exported file.
 *
 * The worker derives column headers directly from the Arrow schema, ensuring
 * that the descriptor's column keys always match the decoded row data — even
 * for grouped views that use legacy "(Group by N)" column naming.
 *
 * The optional `onProgress` callback receives `(processed, total)` on each
 * progress message from the worker — called for every PROGRESS_INTERVAL_ROWS
 * rows streamed and once at the final count.
 */
export const exportToBlob = async (
  viewer: PerspectiveViewer,
  descriptorInput: ExportDescriptorInput,
  onProgress?: (processed: number, total: number) => void
): Promise<Blob> => {
  // Produce a fully-expanded Arrow buffer so collapsed groups never cause
  // data loss. For flat views this is a direct to_arrow() call on the live view.
  // groupBy + rowPaths come from the actually-exported view so the worker's
  // group decode uses the true depth, column names, and null-key handling.
  const { arrow, groupBy, rowPaths } = await fullyExpandedArrow(viewer)
  const fullInput: DescriptorInput = { ...descriptorInput, groupBy }

  const request: ExportRequest = {
    kind: 'export',
    arrow,
    descriptorInput: fullInput,
    rowPaths
  }

  const msg = await runExportWorker(request, onProgress)

  if (msg.kind === 'done') {
    return msg.blob
  }

  if (msg.kind === 'error') {
    throw new Error(msg.message)
  }

  throw new Error('Unknown worker response')
}

/**
 * Produces a CSV string from the viewer's full data set, regardless of the
 * live view's current group-tree collapse state.
 *
 * Mirrors `exportToBlob`: the SAME fully-expanded Arrow + groupBy + rowPaths
 * feed the SAME worker, with `format: 'csv'` so it routes through the shared
 * descriptor + arrow-rows pipeline and the CSV emitter — guaranteeing the CSV
 * has the same translated headers, hidden id columns, totals label, and
 * null-group cells as the xlsx export.
 *
 * Exported for use by the report:csv handler.
 */
export const exportToCsv = async (
  viewer: PerspectiveViewer,
  descriptorInput: ExportDescriptorInput
): Promise<string> => {
  const { arrow, groupBy, rowPaths } = await fullyExpandedArrow(viewer)
  const fullInput: DescriptorInput = { ...descriptorInput, groupBy }

  const request: ExportRequest = {
    kind: 'export',
    arrow,
    descriptorInput: fullInput,
    rowPaths,
    format: 'csv'
  }

  const msg = await runExportWorker(request)

  if (msg.kind === 'csv') {
    return msg.csv
  }

  if (msg.kind === 'error') {
    throw new Error(msg.message)
  }

  throw new Error('Unknown worker response')
}
